import { useState } from "react";
import { AssociateTaskPerformance, AuditTask } from "../../types";

function formatDate(value: string) {
  try {
    return new Date(value).toLocaleDateString("it-IT", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return value;
  }
}

interface TaskPerformanceCardProps {
  row: AssociateTaskPerformance;
  showHeader?: boolean;
}

export default function TaskPerformanceCard({ row, showHeader = true }: TaskPerformanceCardProps) {
  const [expanded, setExpanded] = useState(false);
  const leadRate =
    row.leadsTarget > 0 ? Math.round((row.leadsDone / row.leadsTarget) * 100) : 0;
  const barColor =
    row.overdueActive > 0
      ? "from-red-400 to-rose-500"
      : "from-emerald-400 to-teal-500";

  const initials =
    row.username
      .split(" ")
      .filter(Boolean)
      .map((n) => n[0])
      .join("")
      .toUpperCase() || "?";

  return (
    <div className="rounded-xl border border-secondary/10 bg-secondary/[0.02] p-4">
      {showHeader && (
        <div className="mb-3 flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-secondary to-[#1e2d4a] text-sm font-bold text-white">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="truncate font-extrabold text-secondary">{row.username}</h4>
            {row.overdueActive > 0 && (
              <p className="text-[10px] font-bold uppercase tracking-wider text-red-600">
                {row.overdueActive} task in ritardo
              </p>
            )}
          </div>
        </div>
      )}

      <div className="mb-2 grid grid-cols-4 gap-2 text-center text-xs">
        <div>
          <b className="text-lg text-emerald-600">{row.tasksCompleted}</b>
          <p className="text-emerald-600/80">Completate</p>
        </div>
        <div>
          <b className="text-lg text-secondary/60">{row.tasksCancelled}</b>
          <p className="text-secondary/50">Annullate</p>
        </div>
        <div>
          <b className="text-lg text-indigo-600">{row.tasksActive}</b>
          <p className="text-indigo-600/80">Attive</p>
        </div>
        <div>
          <b className="text-lg text-secondary">{row.avgPercent}%</b>
          <p className="text-secondary/50">Media</p>
        </div>
      </div>

      <div className="mb-1 flex items-center justify-between text-xs text-secondary/60">
        <span>
          Lead: <span className="font-bold text-secondary">{row.leadsDone}</span>
          <span className="text-secondary/40"> / </span>
          {row.leadsTarget}
        </span>
        <span className="font-semibold">Successo chiusure {row.successRate}%</span>
      </div>

      <div className="h-2.5 overflow-hidden rounded-full bg-secondary/10">
        <div
          className={`h-full rounded-full bg-gradient-to-r ${barColor} transition-all`}
          style={{ width: `${leadRate}%` }}
        />
      </div>
      <p className="mt-1 text-right text-[10px] font-semibold text-secondary/60">
        {leadRate}% obiettivo lead
      </p>

      {row.recentClosed.length > 0 && (
        <div className="mt-3 border-t border-secondary/10 pt-3">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex w-full items-center justify-between cursor-pointer text-left text-xs font-bold uppercase tracking-wider text-secondary/50 hover:text-secondary"
            aria-expanded={expanded}
          >
            Ultime task
            <span
              className={`text-secondary/40 transition-transform ${expanded ? "rotate-180" : ""}`}
              aria-hidden
            >
              ▼
            </span>
          </button>
          {expanded && (
            <ul className="mt-2 space-y-2">
              {row.recentClosed.map((task: AuditTask) => (
                <li
                  key={task.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white px-3 py-2 text-xs ring-1 ring-secondary/5"
                >
                  <span className="font-semibold text-secondary">
                    {task.completed_count}/{task.target_count} lead
                  </span>
                  <span className="text-secondary/50">{formatDate(task.due_date)}</span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                      task.status === "completed"
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {task.status === "completed" ? "Completata" : "Annullata"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
